import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { format, parseISO, set } from "date-fns";
import { Form, Formik } from "formik";
import moment from "moment";
import React from "react";
import { DateObject, Calendar as MultiCalendar } from "react-multi-date-picker";
import { useDispatch, useSelector } from "react-redux";
import { useLocalStorage } from "../hooks";
import { useToast } from "../provider/ToastProvider";
import { loadingState, setActionLoading, setGlobalLoading } from "../redux/reducers/loadingReducer";
import "../styles/Calendar.css";
import axiosInstance from "../utils/axiosInstance";
import { getUrlForClasses, getUrlForEvent } from "../utils/confighelpers";
import { TwButton, TwCalendarPicker, TwFormHelperText, TwTimePicker } from "./tailwind";
import { useTranslation } from "react-i18next";
import calender_black from "../assets/calender_black.svg";

export default function ChangeDateTimeModal({
  open,
  handleClose,
  sessionData,
  instanceId,
  handleUpdate,
}) {
  const { t, i18n } = useTranslation();
  const { addToast, addSuccessToast, addErrorToast } = useToast();
  const dispatch = useDispatch();
  const loadingReduxState = useSelector(loadingState);
  const [userToken] = useLocalStorage("userToken");
  const [sessionDetails, setSessionDetails] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      if (open) {
        const sessionDetailsConfig = {
          method: "get",
          url: `${(sessionData.hasOwnProperty("eventId") ? `${getUrlForEvent()}changeEventScheduleDetail` : `${getUrlForClasses()}changeScheduleDetail`)}?reqId=${sessionData._id}&type=${sessionData.isPackage ? "package" : "instance"
            }&instanceId=${instanceId}`,
          headers: {
            "x-access-token": userToken,
          },
        };
        dispatch(setGlobalLoading(true));
        const sessionDetailsRequest = await axiosInstance(sessionDetailsConfig);
        dispatch(setGlobalLoading(false));
        setSessionDetails(sessionDetailsRequest?.data?.data);
      }
    })();
  }, [instanceId, sessionData]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="relative z-50"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform [overflow-hidden] rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="absolute right-0 top-0 pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span className="sr-only">{t("inputFieldsTitle.close")}</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="sm:flex sm:items-center">
              <div className="mt-3 text-center sm:mx-4 sm:mt-0 sm:text-left w-full">
                <DialogTitle as="h3" className="text-xl font-semibold text-gray-900">
                  {t("myEvents.changeSchedule")}
                </DialogTitle>
                <div className="mt-2">
                  <Formik
                    initialValues={{
                      classDate: parseISO(sessionData.hasOwnProperty("eventId") ? sessionData.eventDate : sessionData.classDate),
                      startTime: parseISO(sessionData.hasOwnProperty("eventId") ? sessionData.eventDate : sessionData.classDate),
                      endDateTime: moment.utc(sessionData.eventDate).local().add(sessionData.duration, "minutes").toDate(),
                      endTime: moment.utc(sessionData.eventDate).local().add(sessionData.duration, "minutes").toDate(),
                    }}
                    validate={(values) => {
                      const errors = {};
                      if (!values.classDate) {
                        errors.classDate = t("errors.required");
                      }
                      if (!values.startTime) {
                        errors.startTime = t("errors.required");
                      }
                      if (!values.endTime) {
                        errors.endTime = t("errors.required");
                      }

                      let startDate = values.classDate?.toDate && typeof values.classDate.toDate == "function" ? values.classDate.toDate() : values.classDate
                      let startTime = values.startTime?.toDate && typeof values.startTime.toDate == "function" ? values.startTime.toDate() : values.startTime
                      startTime = moment(moment(startDate).format("YYYY-MM-DD") + " " + moment(startTime).format("HH:mm"), "YYYY-MM-DD HH:mm")
                      let endDateTime = values.endDateTime?.toDate && typeof values.endDateTime.toDate == "function" ? values.endDateTime.toDate() : values.endDateTime
                      let endTime = values.endTime?.toDate && typeof values.endTime.toDate == "function" ? values.endTime.toDate() : values.endTime
                      endTime = moment(moment(endDateTime).format("YYYY-MM-DD") + " " + moment(endTime).format("HH:mm"), "YYYY-MM-DD HH:mm")
                      var duration = moment.duration(moment(endTime).diff(moment(startTime)));
                      var minutes = duration.asMinutes();

                      if (minutes < 1) {
                        errors.endTime = t("errors.startApartMinuteErrorMsg");
                      }
                      return errors;
                    }}
                    onSubmit={async (values, { setSubmitting }) => {
                      setSubmitting(true);

                      const classDateTransformed =
                        values.classDate instanceof DateObject
                          ? values.classDate.toDate()
                          : values.classDate;

                      let startDate = set(values.classDate, {
                        hours: Number(format(values.startTime, "k")),
                        minutes: Number(format(values.startTime, "m")),
                      })

                      let startTime = values.startTime?.toDate && typeof values.startTime.toDate == "function" ? values.startTime.toDate() : values.startTime
                      startTime = moment(moment(startDate).format("YYYY-MM-DD") + " " + moment(startTime).format("HH:mm"), "YYYY-MM-DD HH:mm")
                      let endDateTime = values.endDateTime?.toDate && typeof values.endDateTime.toDate == "function" ? values.endDateTime.toDate() : values.endDateTime
                      let endTime = values.endTime?.toDate && typeof values.endTime.toDate == "function" ? values.endTime.toDate() : values.endTime
                      endTime = moment(moment(endDateTime).format("YYYY-MM-DD") + " " + moment(endTime).format("HH:mm"), "YYYY-MM-DD HH:mm")
                      var duration = moment.duration(moment(endTime).diff(moment(startTime)));
                      var minutes = duration.asMinutes();

                      const body = {
                        // isPackage: sessionData.isPackage,
                        isPackage: false,
                        // scheduleId: sessionDetails._id,
                        scheduleId: instanceId,
                        eventId: sessionData.eventId._id,
                        eventScheduleId: instanceId,
                        eventDate: startDate,
                        meridiem: format(values.startTime, "a"),
                        timing: format(values.startTime, "KK:mm a"),
                        duration: minutes,
                      };
                      // return;

                      const config = {
                        method: "put",
                        url: `${getUrlForEvent()}schedule/update`,
                        headers: {
                          "x-access-token": userToken,
                        },
                        data: body,
                      };
                      dispatch(setActionLoading(true));
                      const request = await axiosInstance(config);
                      dispatch(setActionLoading(false));
                      if (request.status === 200) {
                        if (request.data.statusCode === 200) {
                          addSuccessToast(t("successes.success"));
                          setSubmitting(false);
                          handleClose();
                          handleUpdate();
                        } else {
                          setSubmitting(false);
                          addErrorToast(request.data.message);
                        }
                      }

                      // // return;


                      //     // update event duration


                      //     /** **/
                      //   }
                      // }

                    }}
                  >
                    {({ values, errors, setFieldValue, submitForm, isSubmitting }) => (
                      <Form className="flex flex-col gap-4 w-full">

                        {/* <MultiCalendar
                          className='bookzy-calendar bookzy-border px-2.5 py-1'
                          minDate={new Date()}
                          value={values.classDate}
                          onChange={(date) => {
                            const dateSelected = date.toDate();
                            setFieldValue("classDate", dateSelected);
                          }}
                          weekDays={["S", "M", "T", "W", "T", "F", "S"]}
                          mapDays={({
                            date,
                            today,
                            selectedDate,
                            currentMonth,
                            isSameDate,
                          }) => {
                            let props = {};


                            if (isSameDate(date, today)) {
                              props.className = "default-current-date";
                              props.style = {
                                ...props.style,
                                border: "1px solid #777",
                              };
                            }
                            if (isSameDate(date, selectedDate)) {
                              props.className = "selected-date";
                            }

                            return props;
                          }}
                        /> */}
                        {errors.classDate && (
                          <TwFormHelperText error={true}>{errors.classDate}</TwFormHelperText>
                        )}
                        <div className="flex flex-col sm:flex-row gap-4">
                          <TwCalendarPicker
                            outerContainerClassName="flex-1"
                            // label={formValues.radioValue === "One Time" ? t("myEvents.eventDate") : t("myEvents.startDate")}
                            label={"Start Date & Time"}
                            name={"startDate"}
                            leftIcon={(<img className={""} src={calender_black} alt={""} />)}
                            format="ddd, DD MMM YYYY"
                            value={values.classDate || ""}
                            minDate={moment().subtract(1, "day").toDate()}
                            // minDate={moment().add(1, "day").toDate()}
                            onChange={(newValue) => {
                              const dateSelected = newValue.toDate();
                              setFieldValue("classDate", dateSelected);
                            }}
                            errors={errors} />
                          <TwTimePicker
                            outerContainerClassName="sm:!w-44 sm:!max-w-44 md:!max-w-44"
                            label={<>&nbsp;</>}
                            // label={t("myEvents.selectStartTime")}
                            name={"startTime"}
                            format={"HH:mm"}
                            rightIcon={(
                              <svg className={"size-5 sm:size-4"} width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6.11692 12.978C2.73828 12.978 0 10.2397 0 6.86106C0 3.48242 2.73828 0.744141 6.11692 0.744141C9.49556 0.744141 12.2338 3.48242 12.2338 6.86106C12.2338 10.2397 9.49556 12.978 6.11692 12.978ZM5.54346 6.86106C5.54346 7.05221 5.63904 7.23142 5.79913 7.31744L8.09297 8.84667C8.35581 9.0426 8.71183 8.97092 8.86714 8.70808C9.06308 8.44525 8.99139 8.08922 8.72856 7.91241L6.69038 6.55521V3.61145C6.69038 3.29365 6.43471 3.03799 6.09542 3.03799C5.79913 3.03799 5.52195 3.29365 5.52195 3.61145L5.54346 6.86106Z" fill="#000" />
                              </svg>)}
                            value={values.startTime}
                            onChange={(date, dateParams) => {
                              if (date) {
                                setFieldValue("startTime", date.toDate());
                              } else {
                                setFieldValue("startTime", "");
                              }
                            }}
                            errors={errors} />
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4">
                          <TwCalendarPicker
                            outerContainerClassName="flex-1"
                            // label={formValues.radioValue === "One Time" ? t("myEvents.eventDate") : t("myEvents.startDate")}
                            label={"End Date & Time"}
                            name={"endDateTime"}
                            leftIcon={(<img className={""} src={calender_black} alt={""} />)}
                            format="ddd, DD MMM YYYY"
                            value={values.endDateTime || ""}
                            minDate={moment().subtract(1, "day").toDate()}
                            // minDate={moment().add(1, "day").toDate()}
                            onChange={(newValue) => {
                              const dateSelected = newValue.toDate();
                              setFieldValue("endDateTime", dateSelected);
                            }}
                            errors={errors} />
                          <TwTimePicker
                            outerContainerClassName="sm:!w-44 sm:!max-w-44 md:!max-w-44"
                            label={<>&nbsp;</>}
                            // label={t("myEvents.selectEndTime")}
                            name={"endTime"}
                            format={"HH:mm"}
                            rightIcon={(
                              <svg className={"size-5 sm:size-4"} width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6.11692 12.978C2.73828 12.978 0 10.2397 0 6.86106C0 3.48242 2.73828 0.744141 6.11692 0.744141C9.49556 0.744141 12.2338 3.48242 12.2338 6.86106C12.2338 10.2397 9.49556 12.978 6.11692 12.978ZM5.54346 6.86106C5.54346 7.05221 5.63904 7.23142 5.79913 7.31744L8.09297 8.84667C8.35581 9.0426 8.71183 8.97092 8.86714 8.70808C9.06308 8.44525 8.99139 8.08922 8.72856 7.91241L6.69038 6.55521V3.61145C6.69038 3.29365 6.43471 3.03799 6.09542 3.03799C5.79913 3.03799 5.52195 3.29365 5.52195 3.61145L5.54346 6.86106Z" fill="#000" />
                              </svg>)}
                            value={values.endTime}
                            onChange={(date, dateParams) => {
                              if (date) {
                                setFieldValue("endTime", date.toDate());
                              } else {
                                setFieldValue("endTime", "");
                              }
                            }}
                          // errors={errors}
                          />
                        </div>
                        {errors["endTime"] && (
                          <TwFormHelperText error={true}>{errors["endTime"]}</TwFormHelperText>
                        )}
                        <TwButton
                          variant={"secondary"}
                          onClick={submitForm}
                          disabled={loadingReduxState.actionLoading}
                          actionLoading={(loadingReduxState.actionLoading)}
                        >
                          {t("update")}
                        </TwButton>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
